import { useEffect, useState } from "react";
import { X, Check, Copy, Clock } from "lucide-react";
import type { PopupConfig } from "@/lib/identity/popupConfig";
import { OtpCaptureForm, type IssuedCoupon } from "./OtpCaptureForm";
import { recordDismissal } from "@/lib/identity.functions";
import { useIdentity } from "@/lib/identity/useIdentity";
import { useCartStore } from "@/stores/cartStore";
import { ScratchCard } from "./ScratchCard";
import { Confetti } from "./Confetti";
import { track } from "@/lib/identity/tracker";

function useCountdown(expiresAt: string | undefined) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (!expiresAt) return { label: "", expired: false, urgent: false };
  const ms = Math.max(0, new Date(expiresAt).getTime() - now);
  if (ms <= 0) return { label: "Expired", expired: true, urgent: true };
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  const label = `${pad(h)}h ${pad(m)}m ${pad(s)}s left`;
  return { label, expired: false, urgent: h < 24 };
}

type Props = {
  popup: PopupConfig;
  open: boolean;
  onClose: () => void;
};

/**
 * SmartPopup — brand-aware capture sheet.
 * Reusable from anywhere: render <SmartPopup popup={cfg} open={…} onClose={…} />
 * Or rely on IdentityProvider's global trigger engine.
 */
export function SmartPopup({ popup, open, onClose }: Props) {
  const { anonymousId, refresh } = useIdentity();
  const setCoupon = useCartStore((s) => s.setCoupon);
  const [done, setDone] = useState(false);
  const [issuedCoupon, setIssuedCoupon] = useState<IssuedCoupon>(null);
  const [copied, setCopied] = useState(false);
  const [scratched, setScratched] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);
  const countdown = useCountdown(issuedCoupon?.expiresAt);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isVibe = false;
  const close = () => {
    if (!done && anonymousId) {
      recordDismissal({ data: { anonymousId, popupId: popup.id } }).catch(() => undefined);
    }
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`popup-${popup.id}-title`}
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      onClick={close}
    >
      <div
        data-brand={popup.brand}
        onClick={(e) => e.stopPropagation()}
        className={
          "relative w-full max-w-md overflow-hidden border bg-white shadow-2xl sm:rounded-2xl " +
          (isVibe
            ? "rounded-t-3xl border-pink-200 sm:rounded-3xl"
            : "rounded-t-2xl border-slate-200")
        }
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-slate-500 hover:bg-slate-100"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header banner */}
        <div
          className={
            "px-6 pt-6 pb-4 " +
            (isVibe
              ? "bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50"
              : "bg-gradient-to-br from-[color:var(--brand-soft,#EAF1FF)] to-white")
          }
        >
          <p
            className={
              "text-[11px] font-semibold uppercase tracking-[0.18em] " +
              (isVibe ? "text-pink-600" : "text-[color:var(--brand,#1E5EFF)]")
            }
          >
            {isVibe ? "Vibe Wellness" : "TruWe"} • Founding Member Benefits
          </p>
          <h2
            id={`popup-${popup.id}-title`}
            className={
              "mt-1.5 font-display text-[22px] leading-[1.2] " +
              (isVibe ? "font-bold text-slate-900" : "font-semibold text-slate-900")
            }
          >
            {popup.headline}
          </h2>
          {popup.sub ? <p className="mt-2 text-sm text-slate-600">{popup.sub}</p> : null}
          <div
            className={
              "mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold " +
              (isVibe ? "bg-pink-100 text-pink-700" : "bg-[color:var(--brand,#1E5EFF)]/10 text-[color:var(--brand,#1E5EFF)]")
            }
          >
            <Check className="h-3.5 w-3.5" />
            {popup.offer}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 pt-5">
          {done ? (
            <div className="py-4 text-center">
              <div
                className={
                  "mx-auto flex h-12 w-12 items-center justify-center rounded-full " +
                  (isVibe ? "bg-pink-100 text-pink-600" : "bg-[color:var(--brand,#1E5EFF)]/10 text-[color:var(--brand,#1E5EFF)]")
                }
              >
                <Check className="h-6 w-6" />
              </div>
              <p className="mt-3 font-display text-lg font-semibold text-slate-900">You're in.</p>
              {issuedCoupon ? (
                <>
                  <p className="mt-1 text-sm text-slate-600">
                    Scratch the card to reveal your personal {issuedCoupon.discountPercent}%-off code.
                  </p>
                  <div
                    role="timer"
                    aria-live="polite"
                    className={
                      "mx-auto mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider " +
                      (countdown.expired
                        ? "border-slate-200 bg-slate-100 text-slate-500"
                        : "border-red-200 bg-red-50 text-red-600 animate-pulse")
                    }
                  >
                    <Clock className="h-3.5 w-3.5" />
                    {countdown.expired
                      ? "Offer expired"
                      : `Hurry, only ${countdown.label}`}
                  </div>
                  <div className="relative mx-auto mt-3 max-w-xs">
                    {confettiKey > 0 ? (
                      <Confetti trigger={confettiKey} width={280} height={180} count={50} />
                    ) : null}
                    <ScratchCard
                      width={280}
                      height={130}
                      onScratchStart={() =>
                        track({
                          eventType: "coupon_scratch_started",
                          properties: {
                            code: issuedCoupon.code,
                            popup_id: popup.id,
                            discount_percent: issuedCoupon.discountPercent,
                          },
                        })
                      }
                      onRevealed={() => {
                        setScratched(true);
                        setConfettiKey((k) => k + 1);
                        track({
                          eventType: "coupon_scratch_completed",
                          properties: {
                            code: issuedCoupon.code,
                            popup_id: popup.id,
                            discount_percent: issuedCoupon.discountPercent,
                          },
                        });
                      }}
                      onFallbackReveal={() =>
                        track({
                          eventType: "coupon_scratch_fallback_used",
                          properties: { code: issuedCoupon.code, popup_id: popup.id },
                        })
                      }
                    >
                      <div className="flex w-full items-stretch overflow-hidden rounded-lg bg-white/70">
                        <div className="flex-1 px-3 py-2.5 text-left">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--brand,#1E5EFF)]">
                            Your {issuedCoupon.discountPercent}% off code
                          </p>
                          <p className="font-mono text-base font-bold tracking-wider text-slate-900">
                            {issuedCoupon.code}
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={!scratched || countdown.expired}
                          onClick={() => {
                            if (!scratched || countdown.expired) return;
                            if (typeof navigator !== "undefined" && navigator.clipboard) {
                              navigator.clipboard.writeText(issuedCoupon.code).catch(() => undefined);
                            }
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1800);
                            track({
                              eventType: "coupon_copied",
                              properties: {
                                code: issuedCoupon.code,
                                popup_id: popup.id,
                                source: "popup",
                              },
                            });
                          }}
                          className="flex items-center gap-1 border-l border-[color:var(--brand,#1E5EFF)]/30 bg-white px-3 text-xs font-semibold text-[color:var(--brand,#1E5EFF)] hover:bg-[color:var(--brand,#1E5EFF)]/5 disabled:opacity-40"
                          aria-label="Copy code"
                        >
                          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                          {copied ? "Copied" : "Copy"}
                        </button>
                      </div>
                    </ScratchCard>
                    <p className="mt-2 text-left text-[11px] leading-snug text-slate-500">
                      {scratched
                        ? "Auto-applied to your cart. Single use, prepaid orders only, tied to your mobile number."
                        : "Scratch above to reveal. Applied automatically to your cart."}
                    </p>
                  </div>
                </>
              ) : (
                <p className="mt-1 text-sm text-slate-600">
                  Your welcome offer is ready, check your email.
                </p>
              )}
            </div>
          ) : (
            <OtpCaptureForm
              popup={popup}
              anonymousId={anonymousId}
              onClose={close}
              onSuccess={({ coupon }) => {
                setDone(true);
                setIssuedCoupon(coupon);
                if (coupon) {
                  // Auto-apply to cart so shopper just has to hit checkout.
                  setCoupon({
                    code: coupon.code,
                    discountPercent: coupon.discountPercent,
                    prepaidOnly: coupon.prepaidOnly,
                    expiresAt: coupon.expiresAt,
                  });
                  track({
                    eventType: "coupon_applied",
                    properties: {
                      code: coupon.code,
                      popup_id: popup.id,
                      discount_percent: coupon.discountPercent,
                      source: "popup_autoapply",
                    },
                  });
                }
                refresh();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
